import type { Milestone } from "@shopify-harness/sdk";
import { createFileRoute, type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import ChevronLeftIcon from "lucide-react/dist/esm/icons/chevron-left";
import ChevronRightIcon from "lucide-react/dist/esm/icons/chevron-right";
import PencilIcon from "lucide-react/dist/esm/icons/pencil";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { FilterSelect } from "@/components/data-table/filter-controls";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states/state-views";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  MILESTONE_CATEGORY_CHIP_COLOR,
  MILESTONE_CATEGORY_LABEL,
  MILESTONE_CATEGORY_OPTIONS
} from "@/features/calendar/constants";
import {
  MilestoneFormDialog,
  type MilestoneFormValues
} from "@/features/calendar/milestone-form-dialog";
import { addMonths, currentMonth, formatMonthLabel } from "@/features/calendar/month";
import { MonthGrid } from "@/features/calendar/month-grid";
import { milestoneQueryKeys, milestonesQueryOptions } from "@/features/calendar/queries";
import { type CalendarSearch, calendarSearchSchema } from "@/features/calendar/schemas";
import { formatDate, formatInteger } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/calendar")({
  validateSearch: calendarSearchSchema,
  loaderDeps: ({ search }) => ({ month: search.month ?? currentMonth() }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(milestonesQueryOptions(context.api.milestones, deps.month)),
  pendingComponent: CalendarLoading,
  errorComponent: CalendarError,
  component: CalendarPage
});

/** "YYYY-MM-DD" を「7/2」形式にする。Date を経由せずタイムゾーンのずれを避ける。 */
function formatDayLabel(date: string): string {
  const [, month, day] = date.split("-");
  return `${Number(month)}/${Number(day)}`;
}

/** 期間表示。単日は「7/2」、期間は「7/10〜7/20」。 */
function formatPeriodLabel(milestone: Milestone): string {
  const start = formatDayLabel(milestone.startedOn);
  return milestone.endedOn ? `${start}〜${formatDayLabel(milestone.endedOn)}` : start;
}

interface FormDialogState {
  milestone?: Milestone;
  defaultDate?: string;
}

/**
 * 運用イベントのカレンダー(11章の分析系を支える記録画面)。
 * 施策の実施日をマイルストーンとして登録し、分析時に「実施前後」の傾向差分を比較する基準点にする。
 */
function CalendarPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const data = Route.useLoaderData();
  const { api, queryClient } = Route.useRouteContext();
  const router = useRouter();

  const month = search.month ?? currentMonth();
  const [formDialog, setFormDialog] = useState<FormDialogState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Milestone | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const update = (patch: Partial<CalendarSearch>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const filtered = useMemo(() => {
    const rows =
      search.category === "all"
        ? data.milestones
        : data.milestones.filter((milestone) => milestone.category === search.category);
    return [...rows].sort((a, b) => a.startedOn.localeCompare(b.startedOn));
  }, [data.milestones, search.category]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: milestoneQueryKeys.all });
    await router.invalidate();
  };

  const submitForm = async (values: MilestoneFormValues) => {
    const editing = formDialog?.milestone;
    const title = values.title.trim();
    const description = values.description.trim();
    setSaving(true);
    try {
      if (editing) {
        await api.milestones.update(editing.id, {
          startedOn: values.startedOn,
          // 終了日を空にして保存すると単日イベントに戻る
          endedOn: values.endedOn === "" ? null : values.endedOn,
          category: values.category,
          title,
          description: description === "" ? null : description
        });
        toast.success("マイルストーンを更新しました");
      } else {
        await api.milestones.create({
          startedOn: values.startedOn,
          ...(values.endedOn === "" ? {} : { endedOn: values.endedOn }),
          category: values.category,
          title,
          ...(description === "" ? {} : { description })
        });
        toast.success(`マイルストーンを登録しました（${formatDate(values.startedOn)}）`);
      }
      await refresh();
      setFormDialog(null);
    } catch (error) {
      toast.error(
        editing ? "マイルストーンを更新できませんでした" : "マイルストーンを登録できませんでした",
        { description: error instanceof Error ? error.message : undefined }
      );
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.milestones.delete(deleteTarget.id);
      toast.success("マイルストーンを削除しました");
      await refresh();
      setDeleteTarget(null);
    } catch (error) {
      toast.error("マイルストーンを削除できませんでした", {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setDeleting(false);
    }
  };

  const isFiltered = search.category !== "all";

  return (
    <>
      <PageHeader
        kind="data"
        title="カレンダー"
        description="施策の実施日・期間をマイルストーンとして記録し、分析時に前後比較の基準点にする"
        controls={
          <>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                aria-label="前の月へ"
                onClick={() => update({ month: addMonths(month, -1) })}
              >
                <ChevronLeftIcon aria-hidden="true" />
              </Button>
              <span className="num min-w-24 text-center text-sm font-semibold text-fg">
                {formatMonthLabel(month)}
              </span>
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                aria-label="次の月へ"
                onClick={() => update({ month: addMonths(month, 1) })}
              >
                <ChevronRightIcon aria-hidden="true" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => update({ month: undefined })}
                disabled={month === currentMonth()}
              >
                今月
              </Button>
            </div>
            <FilterSelect
              label="種別"
              value={search.category}
              onChange={(value) => update({ category: value })}
              options={[{ value: "all", label: "すべて" }, ...MILESTONE_CATEGORY_OPTIONS]}
            />
          </>
        }
        action={
          <Button type="button" size="sm" onClick={() => setFormDialog({})}>
            <PlusIcon aria-hidden="true" />
            マイルストーンを登録
          </Button>
        }
      />

      <PageBody density="dense">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Panel
            title={formatMonthLabel(month)}
            count={formatInteger(filtered.length)}
            {...(data.cursor ? { description: "件数が多いため先頭100件のみ表示しています" } : {})}
            flush
          >
            <MonthGrid
              month={month}
              milestones={filtered}
              onAddForDate={(date) => setFormDialog({ defaultDate: date })}
              onSelectMilestone={(milestone) => setFormDialog({ milestone })}
            />
          </Panel>

          <Panel title="この月のマイルストーン" count={formatInteger(filtered.length)} flush>
            {filtered.length > 0 ? (
              <ul className="divide-y divide-line">
                {filtered.map((milestone) => (
                  <li key={milestone.id} className="flex items-start gap-3 px-4 py-3">
                    <span className="num shrink-0 pt-0.5 text-xs text-fg-muted">
                      {formatPeriodLabel(milestone)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            "shrink-0 rounded-sm border px-1 text-2xs text-fg-muted",
                            MILESTONE_CATEGORY_CHIP_COLOR[milestone.category]
                          )}
                        >
                          {MILESTONE_CATEGORY_LABEL[milestone.category]}
                        </span>
                        <span className="truncate text-sm text-fg">{milestone.title}</span>
                      </span>
                      {milestone.description ? (
                        <span className="mt-0.5 block truncate text-xs text-fg-subtle">
                          {milestone.description}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5">
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        aria-label={`「${milestone.title}」を編集`}
                        onClick={() => setFormDialog({ milestone })}
                      >
                        <PencilIcon aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        aria-label={`「${milestone.title}」を削除`}
                        onClick={() => setDeleteTarget(milestone)}
                      >
                        <Trash2Icon aria-hidden="true" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : isFiltered ? (
              <EmptyState
                title="条件に一致するマイルストーンがありません"
                description="種別の絞り込みを解除するか、別の月を表示してください。"
                action={
                  <Button size="sm" variant="outline" onClick={() => update({ category: "all" })}>
                    絞り込みを解除
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="この月のマイルストーンはまだありません"
                description="施策の実施日を登録すると、分析時に「実施前後」でデータ傾向を比較できるようになります。"
                action={
                  <Button size="sm" onClick={() => setFormDialog({})}>
                    <PlusIcon aria-hidden="true" />
                    マイルストーンを登録
                  </Button>
                }
              />
            )}
          </Panel>
        </div>
      </PageBody>

      <MilestoneFormDialog
        open={formDialog !== null}
        onOpenChange={(next) => !next && setFormDialog(null)}
        milestone={formDialog?.milestone}
        defaultDate={formDialog?.defaultDate}
        submitting={saving}
        onSubmit={(values) => void submitForm(values)}
      />

      {/* 削除は取り消せないため、例外的に確認を挟む（原則 7） */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => !next && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>マイルストーンを削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `「${deleteTarget.title}」（${formatPeriodLabel(deleteTarget)}）を完全に削除します。過去のマイルストーンは分析の基準点になるため、誤登録の整理以外では残すことを推奨します。`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleting}
              onClick={() => void runDelete()}
            >
              {deleting ? "削除中…" : "削除"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function CalendarLoading() {
  return (
    <>
      <PageHeader kind="data" title="カレンダー" />
      <PageBody density="dense">
        <Panel title="カレンダー" flush>
          <TableSkeleton rows={6} columns={7} />
        </Panel>
      </PageBody>
    </>
  );
}

function CalendarError({ error, reset }: ErrorComponentProps) {
  return (
    <>
      <PageHeader kind="data" title="カレンダー" />
      <PageBody density="dense">
        <Panel title="カレンダー" flush>
          <ErrorState
            title="マイルストーンを読み込めませんでした"
            message={error.message}
            onRetry={reset}
          />
        </Panel>
      </PageBody>
    </>
  );
}
